import * as Constants from '../constants'

class WebSocketService {
  constructor() {
    this.ws = null
    this.apikey = null
    this.listeners = new Map()
    this.heartbeatInterval = null
    this.isIntentionalClose = false
    this.reconnectAttempts = 0
    // Si true, on n'essaie plus de se reconnecter automatiquement (apikey refusée par /ureq).
    // Reset uniquement lorsqu'une nouvelle apikey est fournie (changement manuel) ou au reload de la page.
    this.unauthorized = false
    this.reconnectTimer = null
  }

  async connect(apikey) {
    if (!apikey) {
      console.warn('WebSocket: pas d\'apikey, connexion ignorée')
      return
    }

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      console.log('WebSocket already connected')
      return
    }

    // Nouvelle apikey explicite → reset du flag unauthorized pour autoriser une nouvelle tentative
    if (apikey !== this.apikey) {
      this.unauthorized = false
    }

    if (this.unauthorized) {
      console.warn('WebSocket: apikey précédemment refusée (401), nouvelle tentative bloquée jusqu\'au prochain changement d\'apikey ou reload')
      return
    }

    this.apikey = apikey
    this.isIntentionalClose = false
    this._notifyListeners('connectionState', 'connecting')

    let session
    try {
      session = await this._requestSession(apikey)
    } catch (error) {
      if (error && error.status === 401) {
        // Apikey refusée : on bloque toute nouvelle tentative pour éviter un ban d'IP
        console.error('WebSocket: /ureq a retourné 401, reconnexion désactivée')
        this.unauthorized = true
        this._notifyListeners('error', { code: 401, message: 'Tablette non autorisée' })
        this._notifyListeners('connectionState', 'disconnected')
        return
      }
      console.error('WebSocket: échec de /ureq, nouvelle tentative dans 5s', error)
      this._notifyListeners('connectionState', 'error')
      this._scheduleReconnect()
      return
    }

    try {
      this.ws = new WebSocket(session.wsUrl, ['bearer.' + session.token])

      this.ws.onopen = () => {
        console.log('WebSocket connected')
        this.reconnectAttempts = 0
        this._startHeartbeat()

        // Send connect message with apikey
        this.send('connect', {})
      }

      this.ws.onmessage = (event) => {
        this._handleMessage(event)
      }

      this.ws.onclose = (event) => {
        this._handleClose(event)
      }

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error)
        this._notifyListeners('connectionState', 'error')
      }
    } catch (error) {
      console.error('WebSocket connection error:', error)
      this._notifyListeners('connectionState', 'error')
      this._scheduleReconnect()
    }
  }

  async _requestSession(apikey) {
    const response = await fetch(Constants.ureqUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ apikey })
    })

    if (!response.ok) {
      const err = new Error('ureq HTTP ' + response.status)
      err.status = response.status
      throw err
    }

    const data = await response.json()
    if (!data || !data.wsUrl || !data.token) {
      throw new Error('ureq: réponse invalide')
    }
    return data
  }

  disconnect() {
    this.isIntentionalClose = true
    this._stopHeartbeat()
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    if (this.ws) {
      this.ws.close()
      this.ws = null
    }
    this._notifyListeners('connectionState', 'disconnected')
  }

  send(dataType, data) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn('WebSocket not connected, cannot send message')
      return false
    }

    const message = {
      apikey: this.apikey,
      data_type: dataType,
      data: data
    }

    try {
      this.ws.send(JSON.stringify(message))
      return true
    } catch (error) {
      console.error('Failed to send WebSocket message:', error)
      return false
    }
  }

  subscribe(messageType, callback) {
    if (!this.listeners.has(messageType)) {
      this.listeners.set(messageType, new Set())
    }
    this.listeners.get(messageType).add(callback)
  }

  unsubscribe(messageType, callback) {
    if (this.listeners.has(messageType)) {
      this.listeners.get(messageType).delete(callback)
    }
  }

  _notifyListeners(messageType, data) {
    if (this.listeners.has(messageType)) {
      this.listeners.get(messageType).forEach(callback => {
        try {
          callback(data)
        } catch (error) {
          console.error('Error in listener callback:', error)
        }
      })
    }
  }

  _startHeartbeat() {
    this._stopHeartbeat()
    this.heartbeatInterval = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.send('ping', {})
      }
    }, Constants.heartbeatInterval)
  }

  _stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval)
      this.heartbeatInterval = null
    }
  }

  _handleMessage(event) {
    try {
      const message = JSON.parse(event.data)
      const { data_type, data } = message

      switch (data_type) {
        case 'pong':
          // Heartbeat response received
          break
        case 'config':
          this._notifyListeners('connectionState', 'connected')
          this._notifyListeners('config', data)
          break
        case 'transactions':
          this._notifyListeners('transactions', data)
          break
        case 'articles':
          this._notifyListeners('articles', data)
          break
        case 'error':
          console.error('Server error:', data)
          this._notifyListeners('error', data)
          // Check for authentication error
          if (data.code === 401 || data.code === 'unauthorized') {
            this.unauthorized = true
            this.isIntentionalClose = true
            this.disconnect()
          }
          break
        default:
          console.log('Unknown message type:', data_type, data)
      }
    } catch (error) {
      console.error('Failed to parse WebSocket message:', error)
    }
  }

  _handleClose(event) {
    console.log('WebSocket closed:', event.code, event.reason)
    this._stopHeartbeat()
    this._notifyListeners('connectionState', 'disconnected')

    if (!this.isIntentionalClose && !this.unauthorized) {
      this._scheduleReconnect()
    }
  }

  _scheduleReconnect() {
    if (this.unauthorized || !this.apikey) {
      return
    }
    if (this.reconnectTimer) {
      return
    }
    this.reconnectAttempts++
    console.log(`Reconnecting in 5 seconds... (attempt ${this.reconnectAttempts})`)
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      this.connect(this.apikey)
    }, 5000)
  }
}

export default new WebSocketService()
